"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import type { BlogPostMeta } from "@/lib/blog";

export default function BlogList({ posts }: { posts: BlogPostMeta[] }) {
  const { t, locale } = useTranslation();

  return (
    <main className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-4">
            {t("blog.label")}
          </div>
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-heading font-black text-text-primary leading-[0.9] tracking-tighter uppercase mb-6">
            {t("blog.heading")}
          </h1>
          <p className="text-lg sm:text-xl text-text-muted max-w-2xl">
            {t("blog.subtitle")}
          </p>
        </div>

        {posts.length === 0 ? (
          <div className="border border-border-faint bg-bg-card p-12 text-center">
            <div className="text-6xl font-heading text-mint-text/20 mb-4">{"{ }"}</div>
            <h2 className="text-xl font-bold text-text-primary mb-3">{t("blog.empty.title")}</h2>
            <p className="text-text-faint text-sm max-w-md mx-auto">
              {t("blog.empty.text")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {posts.map((post) => (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="group border border-border-faint bg-bg-card hover:border-mint-text/30 transition-all flex flex-col overflow-hidden"
              >
                {post.coverImage && (
                  <div className="relative w-full h-48 overflow-hidden border-b border-border-faint">
                    <Image
                      src={post.coverImage}
                      alt={post.coverImageAlt ?? post.title}
                      fill
                      className="object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-card/60 to-transparent" />
                    {post.coverImageAiGenerated && (
                      <div className="absolute bottom-3 right-3 text-[9px] font-mono text-white/80 uppercase tracking-widest bg-black/50 px-2 py-1">
                        {t("common.aiGeneratedImage")}
                      </div>
                    )}
                  </div>
                )}

                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-mint-text font-mono text-[10px] tracking-[0.2em] uppercase border border-mint-text/30 px-2 py-1">
                      {locale === "en" && post.categoryEn ? post.categoryEn : post.category}
                    </span>
                    <span className="text-text-faintest font-mono text-[10px] uppercase tracking-widest">
                      {post.readingTime}
                    </span>
                  </div>

                  <h2 className="text-xl sm:text-2xl font-heading font-black text-text-primary tracking-tighter uppercase mb-4 group-hover:text-mint-text transition-colors leading-tight">
                    {locale === "en" && post.titleEn ? post.titleEn : post.title}
                  </h2>

                  <p className="text-text-muted text-sm leading-relaxed mb-6 flex-1">
                    {locale === "en" && post.excerptEn ? post.excerptEn : post.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-6 border-t border-border-faint">
                    <span className="text-text-faintest font-mono text-[10px] uppercase tracking-widest">
                      {new Date(post.date).toLocaleDateString(locale === "de" ? "de-AT" : "en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-mint-text text-xs font-mono uppercase tracking-widest group-hover:gap-3 transition-all">
                      {t("blog.readMore")} <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
